import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGetItemQuery } from '../store/docApi';
import { useSelector } from 'react-redux';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Stack,
  Chip,
  Drawer,
  useTheme,
  useMediaQuery,
  Tooltip,
  Collapse,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ViewSidebar as ViewSidebarIcon,
  ViewSidebarOutlined as ViewSidebarOutlinedIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import MotifDetails from '../Motif/MotifDetails';
import LayoutDetails from '../Layout/LayoutDetails';
import ColorwayDetails from '../Colorway/ColorwayDetails';
import PatternDetails from '../Pattern/PatternDetails';
import ChartEditor from '../components/charts/editor/ChartEditor';
import WorkspaceDetails from './WorkspaceDetails';
import PartDetails from '../Part/PartDetails';
import Motifs from '../Motif/Motifs';
import Layouts from '../Layout/Layouts';
import Parts from '../Part/Parts';
import Colorways from '../Colorway/Colorways';
import Patterns from '../Pattern/Patterns';
import Instructions from '../Instructions/Instructions';
import UserMenu from '../components/app/UserMenu';
import Notes from '../Note/Notes';
import PictureManager from '../components/widgets/PictureManager';
import Bibliography from '../Reference/Bibliography';
import NoteIcon from '@mui/icons-material/Note';
import ImageIcon from '@mui/icons-material/Image';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PaletteIcon from '@mui/icons-material/Palette';
import CategoryIcon from '@mui/icons-material/Category';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import LayersIcon from '@mui/icons-material/Layers';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import InfoIcon from '@mui/icons-material/Info';
import { objectToArray } from '../utils';

export default function WorkspaceEditor() {
  const { pk, id, type, subId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);
  const [isStickyMode, setIsStickyMode] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    motifs: false,
    colorways: false,
    layouts: false,
    patterns: false,
    parts: false,
    pictures: false,
    notes: false,
    bibliography: false
  });
  const userData = useSelector((state) => state.user.userData);

  // Fetch the selected workspace's details
  const { data: selectedWorkspace, isLoading: isLoadingSelected } = useGetItemQuery({ label: 'workspace', pk, id }, { skip: !pk || !id });

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleViewModeToggle = () => {
    setIsStickyMode(!isStickyMode);
    if (!isStickyMode) {
      setIsDrawerOpen(true);
    } else {
      setIsDrawerOpen(false);
    }
  };

  const handleSectionToggle = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleChipClick = (section) => {
    if (!isStickyMode) {
      setIsDrawerOpen(false);
    }
    navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/${section}`);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error.message);
    }
  };

  const getSections = () => {
    if (!selectedWorkspace) return [];

    return [
      {
        id: 'patterns',
        title: 'Patterns',
        items: typeof selectedWorkspace.patterns === 'object' ? objectToArray(selectedWorkspace.patterns) : selectedWorkspace.patterns || [],
        icon: <LayersIcon sx={{ color: theme.palette.text.secondary }} />,
        getLabel: (item) => item.name || 'Unnamed Pattern',
        getSubLabel: (item) => `${item.id} `,
        onClick: (item) => {
          navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/patterns/${item.id}`);
          if (!isStickyMode) {
            setIsDrawerOpen(false);
          }
        }
      },
      {
        id: 'parts',
        title: 'Parts',
        items: typeof selectedWorkspace.parts === 'object' ? objectToArray(selectedWorkspace.parts) : selectedWorkspace.parts || [],
        icon: <BuildCircleIcon sx={{ color: theme.palette.text.secondary }} />,
        getLabel: (item) => item.name || 'Unnamed Part',
        getSubLabel: (item) => `${item.id} `,
        onClick: (item) => {
          navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/parts/${item.id}`);
          if (!isStickyMode) {
            setIsDrawerOpen(false);
          }
        }
      },
      {
        id: 'motifs',
        title: 'Motifs',
        items: typeof selectedWorkspace.motifs === 'object' ? objectToArray(selectedWorkspace.motifs) : selectedWorkspace.motifs || [],
        icon: <CategoryIcon sx={{ color: theme.palette.text.secondary }} />,
        getLabel: (item) => item.name || 'Unnamed Motif',
        getSubLabel: (item) => `${item.id} ${item.chart?.width || 0}x${item.chart?.height || 0}`,
        onClick: (item) => {
          navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/motifs/${item.id}`);
          if (!isStickyMode) {
            setIsDrawerOpen(false);
          }
        }
      },
      {
        id: 'colorways',
        title: 'Colorways',
        items: typeof selectedWorkspace.colorways === 'object' ? objectToArray(selectedWorkspace.colorways) : selectedWorkspace.colorways || [],
        icon: <PaletteIcon sx={{ color: theme.palette.text.secondary }} />,
        getLabel: (item) => item.name || 'Unnamed Colorway',
        getSubLabel: (item) => `${item.id} ${item.colors?.length || 0} colors`,
        onClick: (item) => {
          navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/colorways/${item.id}`);
          if (!isStickyMode) {
            setIsDrawerOpen(false);
          }
        }
      },
      {
        id: 'layouts',
        title: 'Layouts',
        items: typeof selectedWorkspace.layouts === 'object' ? objectToArray(selectedWorkspace.layouts) : selectedWorkspace.layouts || [],
        icon: <ViewModuleIcon sx={{ color: theme.palette.text.secondary }} />,
        getLabel: (item) => item.name || 'Unnamed Layout',
        getSubLabel: (item) => `${item.id} ${item.width || 0}x${item.height || 0}`,
        onClick: (item) => {
          navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/layouts/${item.id}`);
          if (!isStickyMode) {
            setIsDrawerOpen(false);
          }
        }
      },
      {
        id: 'notes',
        title: 'Notes',
        items: [],
        icon: <NoteIcon sx={{ color: theme.palette.text.secondary }} />,
        getLabel: () => 'Notes',
        getSubLabel: () => '',
        onClick: () => {
          navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/notes`);
          if (!isStickyMode) {
            setIsDrawerOpen(false);
          }
        }
      },
      {
        id: 'bibliography',
        title: 'Bibliography',
        items: [],
        icon: <MenuBookIcon sx={{ color: theme.palette.text.secondary }} />,
        getLabel: () => 'Bibliography',
        getSubLabel: () => '',
        onClick: () => {
          navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/bibliography`);
          if (!isStickyMode) {
            setIsDrawerOpen(false);
          }
        }
      },
    ];
  };

  const renderMasterList = () => {
    const isItemSelected = (itemId, itemType) => {
      if (itemType === 'parts' || itemType === 'patterns' || itemType === 'motifs' || itemType === 'colorways' || itemType === 'layouts') {
        return type === itemType && itemId === subId;
      } else {
        return type === itemType && itemId === selectedWorkspace[itemType][parseInt(subId)]?.id;
      }
    };

    const sections = getSections();

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: 1,
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            height: 48,
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <IconButton onClick={() => navigate('/workspaces')} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary'
                  }
                }}
                onClick={() => navigate('/workspaces')}
              >
                Back to workspaces
              </Typography>
            </Box>
            {!isMobile && (
              <Tooltip title={isStickyMode ? 'Switch to collapsible panel' : 'Switch to sticky panel'}>
                <IconButton onClick={handleViewModeToggle} size="small">
                  {isStickyMode ? <ViewSidebarOutlinedIcon /> : <ViewSidebarIcon />}
                </IconButton>
              </Tooltip>
            )}
            {(isMobile || !isStickyMode) && (
              <IconButton size="small" onClick={handleDrawerToggle}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Box>

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {/* Details section as a simple list item */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}`);
                if (!isStickyMode) {
                  setIsDrawerOpen(false);
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    <Typography variant="subtitle2">Details</Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
          <Divider />

          {/* Instructions section */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/instructions`);
                if (!isStickyMode) {
                  setIsDrawerOpen(false);
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MenuBookIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    <Typography variant="subtitle2">Instructions</Typography>
                    {selectedWorkspace.steps && (
                      <Chip
                        size="small"
                        label={`${selectedWorkspace.steps.length} steps`}
                        sx={{
                          ml: 'auto',
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText'
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
          <Divider />

          {/* Pictures section */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}/pictures`);
                if (!isStickyMode) {
                  setIsDrawerOpen(false);
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    <Typography variant="subtitle2">Pictures</Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
          <Divider />

          {/* Collapsible sections */}
          {sections.map((section) => (
            <React.Fragment key={section.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (section.items.length > 0 && !expandedSections[section.id]) {
                      handleSectionToggle(section.id);
                    }
                    handleChipClick(section.id);
                  }}
                  sx={{ py: 0.5, pr: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 24 }}>
                          {React.cloneElement(
                            section.icon,
                            section.items && section.items.length === 0
                              ? { sx: { color: theme.palette.secondary.light } }
                              : { sx: { color: theme.palette.primary.main } }
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: 24 }}>
                          {section.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={section.items.length}
                          sx={{
                            ml: 'auto',
                            cursor: 'pointer',
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            height: 20,
                            '& .MuiChip-label': {
                              px: 1,
                              fontSize: '0.75rem'
                            },
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                              color: 'primary.contrastText',
                              boxShadow: 2
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChipClick(section.id);
                          }}
                        />
                      </Box>
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSectionToggle(section.id);
                    }}
                    sx={{ ml: 1 }}
                  >
                    {expandedSections[section.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </ListItemButton>
              </ListItem>
              <Collapse in={expandedSections[section.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ backgroundColor: 'background.default' }}>
                  {section.items.map((item, index) => (
                    <ListItem key={index} disablePadding sx={{ pl: 2 }}>
                      <ListItemButton
                        onClick={() => section.onClick(item)}
                        selected={isItemSelected(item.id, section.id)}
                        sx={{
                          py: 0.5,
                          '&.Mui-selected': {
                            backgroundColor: 'action.selected',
                            '&:hover': {
                              backgroundColor: 'action.selected'
                            }
                          }
                        }}
                      >
                        <ListItemText 
                          primary={
                            <Typography variant="body2">
                              {section.getLabel(item)}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption">
                              {section.getSubLabel(item)}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );
  };

  const renderHeader = () => {
    if (!selectedWorkspace) return null;

    return (
      <Box
        sx={{
          height: 48,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {(isMobile || !isStickyMode) && (
            <IconButton onClick={handleDrawerToggle} size="small">
              <MenuIcon />
            </IconButton>
          )}
          <Typography 
            variant="subtitle1" 
            onClick={() => navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}`)}
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {selectedWorkspace.name}
          </Typography>
        </Box>
        {userData && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <UserMenu userData={userData} onLogout={handleLogout} />
          </Box>
        )}
      </Box>
    );
  };

  const renderDetailContent = () => {
    // If we're viewing a specific item (motif, colorway, etc.)
    if (type && subId !== undefined) {
      const itemProps = {
        item: selectedWorkspace,
        index: subId, // pass subId as index for all
        subId: subId, // pass subId as id for all
        onBack: () => navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}`)
      };

      let content;
      switch (type) {
        case 'motifs':
          content = location.pathname.includes('/edit') ? <ChartEditor {...itemProps} /> : <MotifDetails {...itemProps} />;
          break;
        case 'layouts':
          content = <LayoutDetails {...itemProps} />;
          break;
        case 'colorways':
          content = <ColorwayDetails {...itemProps} />;
          break;
        case 'patterns':
          content = <PatternDetails {...itemProps} />;
          break;
        case 'parts':
          content = <PartDetails {...itemProps} />;
          break;
        default:
          return null;
      }

      return <Box sx={{ p: 2 }}>{content}</Box>;
    }

    // If we're viewing a section (from chip click or direct navigation)
    if (type) {
      const itemProps = {
        item: selectedWorkspace,
        onBack: () => navigate(`/workspaces/${selectedWorkspace.pk}/${selectedWorkspace.id}`)
      };
      let content;
      switch (type) {
        case 'motifs':
          content = <Motifs {...itemProps} />;
          break;
        case 'layouts':
          content = <Layouts {...itemProps} />;
          break;
        case 'colorways':
          content = <Colorways {...itemProps} />;
          break;
        case 'patterns':
          content = <Patterns {...itemProps} />;
          break;
        case 'parts':
          content = <Parts {...itemProps} />;
          break;
        case 'instructions':
          content = <Instructions {...itemProps} />;
          break;
        case 'notes':
          content = <Notes project={selectedWorkspace} connectionIndex={0} />;
          break;
        case 'pictures':
          content = <PictureManager item={selectedWorkspace} />;
          break;
        case 'bibliography':
          content = <Bibliography item={selectedWorkspace} />;
          break;
        default:
          return null;
      }
      return <Box sx={{ p: 2 }}>{content}</Box>;
    }

    // Default workspace details view using WorkspaceDetails component
    return (
      <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
        <WorkspaceDetails />
      </Box>
    );
  };

  if (isLoadingSelected) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedWorkspace) {
    return (
      <Box sx={{ 
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: 'background.default'
      }}>
        <Typography variant="h6" color="text.secondary">
          Workspace Not Found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          The workspace you're looking for doesn't exist or you don't have access to it.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/workspaces')}
        >
          Back to Workspaces
        </Button>
      </Box>
    );
  }

  const drawer = <Box sx={{ width: 300, height: '100%' }}>{renderMasterList()}</Box>;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        bgcolor: 'background.default'
      }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={isDrawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 300,
              boxSizing: 'border-box',
              height: '100%',
              position: 'relative'
            }
          }}
        >
          {drawer}
        </Drawer>
      ) : isStickyMode ? (
        <Box sx={{ width: 300, flexShrink: 0 }}>{drawer}</Box>
      ) : (
        <Drawer
          variant="temporary"
          anchor="left"
          open={isDrawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 300,
              boxSizing: 'border-box',
              height: '100vh',
              position: 'fixed',
              top: 0,
              borderRadius: 0
            }
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {renderHeader()}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            position: 'relative'
          }}
        >
          {renderDetailContent()}
        </Box>
      </Box>
    </Box>
  );
}
